import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../services/api';
import { withWsToken } from '../services/wsAuth';
import {
  FiDownload, FiUpload, FiX, FiCheck, FiAlertTriangle,
  FiFileText, FiChrome, FiGlobe
} from 'react-icons/fi';
import './ImportExport.css';

function ImportExport({ onClose, onImportComplete }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('import');
  const [importSource, setImportSource] = useState('chrome');
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  // Thread state for progress tracking
  const [threadId, setThreadId] = useState(null);
  const [threadProgress, setThreadProgress] = useState(0);
  const [threadMessage, setThreadMessage] = useState('');
  const [threadStatus, setThreadStatus] = useState(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const wsRef = useRef(null);

  // Poll thread status or listen via WebSocket
  const pollThreadStatus = useCallback(async (tid) => {
    const token = localStorage.getItem('authToken');
    try {
      const res = await axios.get(`${API_BASE}/api/data/thread/${tid}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      const thread = res.data;
      setThreadProgress(thread.progress || 0);
      setThreadMessage(thread.message || '');
      setThreadStatus(thread.status);

      if (thread.status === 'completed' || thread.status === 'failed' || thread.status === 'cancelled') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return thread;
      }
    } catch (err) {
      console.error('Error polling thread:', err);
    }
    return null;
  }, []);

  const startThreadPolling = useCallback((tid) => {
    setThreadId(tid);
    setThreadProgress(0);
    setThreadMessage('Iniciando...');
    setThreadStatus('queued');

    // Try WebSocket first for real-time updates
    try {
      // Mismo origen que la página: funciona en securegatecard.nordicbyte.co.uk
      // y en panel.securegatecard.nordicbyte.co.uk sin distinción por host.
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      const ws = new WebSocket(withWsToken(wsUrl));
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'thread_update' && msg.data?.threadId === tid) {
            setThreadProgress(msg.data.progress || 0);
            setThreadMessage(msg.data.message || '');
            setThreadStatus(msg.data.status);

            if (msg.data.status === 'completed' || msg.data.status === 'failed' || msg.data.status === 'cancelled') {
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
            }
          }
        } catch (e) { /* ignore parse errors */ }
      };

      ws.onerror = () => {
        // Fallback to polling if WS fails
        ws.close();
      };

      ws.onclose = () => {
        wsRef.current = null;
      };
    } catch (e) {
      // WebSocket not available, use polling only
    }

    // Polling as fallback/supplement (every 2s)
    pollIntervalRef.current = setInterval(() => pollThreadStatus(tid), 2000);
    // Initial poll
    setTimeout(() => pollThreadStatus(tid), 500);
  }, [pollThreadStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const cancelThread = async () => {
    if (!threadId) return;
    const token = localStorage.getItem('authToken');
    try {
      await axios.post(`${API_BASE}/api/data/thread/${threadId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreadStatus('cancelled');
      setThreadMessage('Cancelado por el usuario');
    } catch (err) {
      console.error('Error cancelling thread:', err);
    }
  };

  const importSources = [
    { id: 'chrome', name: 'Google Chrome', icon: FiChrome },
    { id: 'firefox', name: 'Mozilla Firefox', icon: FiGlobe },
    { id: 'edge', name: 'Microsoft Edge', icon: FiGlobe },
    { id: 'safari', name: 'Safari', icon: FiGlobe },
    { id: 'bitwarden', name: 'Bitwarden', icon: FiGlobe },
    { id: 'bitwarden-json', name: 'Bitwarden (JSON)', icon: FiGlobe },
    { id: '1password', name: '1Password (CSV)', icon: FiGlobe },
    { id: '1password-1pux', name: '1Password (1PUX)', icon: FiGlobe },
    { id: 'lastpass', name: 'LastPass', icon: FiGlobe },
    { id: 'csv', name: t('importExport.genericCSV'), icon: FiFileText },
  ];

  // 1Password category UUIDs mapping
  const ONEPASSWORD_CATEGORIES = {
    '001': 'login',
    '002': 'card',
    '003': 'secureNote',
    '004': 'identity',
    '005': 'password',
    '006': 'document',
    '100': 'softwareLicense',
    '101': 'bankAccount',
    '102': 'database',
    '103': 'driverLicense',
    '104': 'outdoorLicense',
    '105': 'membership',
    '106': 'passport',
    '107': 'rewardProgram',
    '108': 'socialSecurityNumber',
    '109': 'wirelessRouter',
    '110': 'server',
    '111': 'emailAccount',
    '112': 'apiCredential',
    '113': 'medicalRecord',
    '114': 'sshKey',
    '115': 'cryptoWallet'
  };

  // Bitwarden type mapping
  const BITWARDEN_TYPES = {
    1: 'login',
    2: 'secureNote',
    3: 'card',
    4: 'identity'
  };

  // Smart type detection based on content
  const detectEntryType = (entry) => {
    const { title = '', username = '', password = '', url = '', notes = '', cardNumber, identityName } = entry;
    const titleLower = (title || '').toLowerCase();
    const notesLower = (notes || '').toLowerCase();
    const urlLower = (url || '').toLowerCase();

    // Already has a specific type
    if (entry.detectedType && entry.detectedType !== 'login' && entry.detectedType !== 'note') {
      return entry.detectedType;
    }

    // Card detection
    if (cardNumber || titleLower.includes('card') || titleLower.includes('visa') || 
        titleLower.includes('mastercard') || titleLower.includes('amex')) {
      return 'card';
    }

    // Identity detection
    if (identityName || titleLower.includes('identity') || titleLower.includes('passport') ||
        titleLower.includes('driver') || titleLower.includes('license')) {
      return 'identity';
    }

    // SSH Key detection
    if (titleLower.includes('ssh') || (password && (password.startsWith('ssh-rsa') || 
        password.startsWith('ssh-ed25519') || password.includes('BEGIN OPENSSH')))) {
      return 'sshKey';
    }

    // API Key detection
    if (titleLower.includes('api') || titleLower.includes('token') || titleLower.includes('key') ||
        (password && !username && (password.length > 30 || password.includes('_') || password.includes('-')))) {
      if (!username && password) return 'apiKey';
    }

    // Database detection
    if (titleLower.includes('database') || titleLower.includes('mysql') || 
        titleLower.includes('postgres') || titleLower.includes('mongodb')) {
      return 'database';
    }

    // OAuth detection
    if (urlLower.includes('oauth') || titleLower.includes('oauth') || 
        notesLower.includes('client_id') || notesLower.includes('client_secret')) {
      return 'oauth';
    }

    // Secure Note detection - no username, no password, or only notes
    if (!username && !password && notes) {
      return 'secureNote';
    }

    // Password only (no username, no URL)
    if (!username && password && !url) {
      return 'password';
    }

    // Default to login if has username or password with URL
    if (username || (password && url)) {
      return 'login';
    }

    // If only has notes, it's a secure note
    if (notes && !username && !password) {
      return 'secureNote';
    }

    return 'login';
  };

  const exportFormats = [
    { id: 'csv', name: 'CSV', description: t('importExport.csvDesc') },
    { id: 'json', name: 'JSON', description: t('importExport.jsonDesc') },
    { id: 'encrypted', name: t('importExport.encryptedBackup'), description: t('importExport.encryptedDesc') },
  ];

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const entry = {};
      
      headers.forEach((header, index) => {
        entry[header] = values[index] || '';
      });

      // Map common CSV fields to our format
      data.push({
        title: entry.name || entry.title || entry.url || 'Imported Entry',
        username: entry.username || entry.login || entry.email || entry.user || '',
        password: entry.password || entry.pass || '',
        url: entry.url || entry.website || entry.site || '',
        notes: entry.notes || entry.note || entry.comments || '',
        type: 'login'
      });
    }

    return data;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const parseBitwarden = (json) => {
    try {
      const data = JSON.parse(json);
      const items = data.items || data.logins || [];
      
      return items.map(item => {
        // Bitwarden type: 1=login, 2=secureNote, 3=card, 4=identity
        const bitwardenType = BITWARDEN_TYPES[item.type] || 'login';
        
        let entry = {
          title: item.name || 'Imported Entry',
          username: '',
          password: '',
          url: '',
          notes: item.notes || '',
          tags: [],
          favorite: item.favorite || false,
          type: bitwardenType
        };

        // Parse folder as tag
        if (item.folderId && data.folders) {
          const folder = data.folders.find(f => f.id === item.folderId);
          if (folder?.name) entry.tags.push(folder.name);
        }

        // Parse based on type
        if (item.login) {
          entry.username = item.login.username || '';
          entry.password = item.login.password || '';
          entry.url = item.login.uris?.[0]?.uri || '';
          entry.otpAuth = item.login.totp || '';
        }

        if (item.card) {
          entry.type = 'card';
          entry.cardData = {
            cardholderName: item.card.cardholderName || '',
            cardNumber: item.card.number || '',
            expiryMonth: item.card.expMonth || '',
            expiryYear: item.card.expYear || '',
            cvv: item.card.code || '',
            brand: item.card.brand || ''
          };
        }

        if (item.identity) {
          entry.type = 'identity';
          entry.identityData = {
            firstName: item.identity.firstName || '',
            lastName: item.identity.lastName || '',
            email: item.identity.email || '',
            phone: item.identity.phone || '',
            address: [
              item.identity.address1,
              item.identity.address2,
              item.identity.address3
            ].filter(Boolean).join(', '),
            city: item.identity.city || '',
            state: item.identity.state || '',
            postalCode: item.identity.postalCode || '',
            country: item.identity.country || '',
            company: item.identity.company || '',
            ssn: item.identity.ssn || '',
            passportNumber: item.identity.passportNumber || '',
            licenseNumber: item.identity.licenseNumber || ''
          };
        }

        if (item.secureNote) {
          entry.type = 'secureNote';
        }

        // Parse custom fields
        if (item.fields && item.fields.length > 0) {
          let additionalNotes = '';
          for (const field of item.fields) {
            if (field.name && field.value) {
              additionalNotes += `${field.name}: ${field.value}\n`;
            }
          }
          if (additionalNotes) {
            entry.additionalNotes = additionalNotes;
          }
        }

        return entry;
      });
    } catch (e) {
      console.error('Error parsing Bitwarden JSON:', e);
      return [];
    }
  };

  // Bitwarden CSV parser
  const parseBitwardenCSV = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    // Bitwarden CSV: folder,favorite,type,name,notes,fields,reprompt,login_uri,login_username,login_password,login_totp
    const folderIdx = headers.findIndex(h => h === 'folder');
    const favoriteIdx = headers.findIndex(h => h === 'favorite');
    const typeIdx = headers.findIndex(h => h === 'type');
    const nameIdx = headers.findIndex(h => h === 'name');
    const notesIdx = headers.findIndex(h => h === 'notes');
    const fieldsIdx = headers.findIndex(h => h === 'fields');
    const uriIdx = headers.findIndex(h => h === 'login_uri');
    const usernameIdx = headers.findIndex(h => h === 'login_username');
    const passwordIdx = headers.findIndex(h => h === 'login_password');
    const totpIdx = headers.findIndex(h => h === 'login_totp');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 4) {
        const entryType = typeIdx >= 0 ? values[typeIdx]?.toLowerCase() : 'login';
        const tags = [];
        if (folderIdx >= 0 && values[folderIdx]) tags.push(values[folderIdx]);

        data.push({
          title: nameIdx >= 0 ? values[nameIdx] : 'Imported Entry',
          username: usernameIdx >= 0 ? values[usernameIdx] : '',
          password: passwordIdx >= 0 ? values[passwordIdx] : '',
          url: uriIdx >= 0 ? values[uriIdx] : '',
          notes: notesIdx >= 0 ? values[notesIdx] : '',
          otpAuth: totpIdx >= 0 ? values[totpIdx] : '',
          tags: tags,
          favorite: favoriteIdx >= 0 && values[favoriteIdx] === '1',
          type: entryType === 'note' ? 'secureNote' : entryType || 'login'
        });
      }
    }

    return data;
  };

  const parse1Password = (content) => {
    try {
      // Check if it's CSV format (starts with header row)
      const trimmed = content.trim();
      if (trimmed.startsWith('Title') || trimmed.startsWith('"Title') || 
          trimmed.startsWith('title') || trimmed.startsWith('"title')) {
        // CSV format
        return parse1PasswordCSV(content);
      }
      
      // JSON format - use the new parser
      const data = JSON.parse(content);
      return parse1PasswordJSON(data);
    } catch (e) {
      console.error('Error parsing 1Password:', e);
      return [];
    }
  };

  const parse1PasswordCSV = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Parse header to find column indices
    // 1Password CSV format: Title,Url,Username,Password,OTPAuth,Favorite,Archived,Tags,Notes
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const titleIdx = headers.findIndex(h => h === 'title' || h === 'name');
    const urlIdx = headers.findIndex(h => h === 'url' || h === 'website' || h === 'location');
    const usernameIdx = headers.findIndex(h => h === 'username' || h === 'email' || h === 'login');
    const passwordIdx = headers.findIndex(h => h === 'password' || h === 'pass');
    const otpAuthIdx = headers.findIndex(h => h === 'otpauth' || h === 'otp' || h === 'totp' || h === '2fa');
    const favoriteIdx = headers.findIndex(h => h === 'favorite' || h === 'favourites' || h === 'starred');
    const archivedIdx = headers.findIndex(h => h === 'archived' || h === 'archive' || h === 'deleted');
    const tagsIdx = headers.findIndex(h => h === 'tags' || h === 'tag' || h === 'folder' || h === 'category');
    const notesIdx = headers.findIndex(h => h === 'notes' || h === 'note' || h === 'comments' || h === 'comment');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 2) {
        // Skip archived entries
        const isArchived = archivedIdx >= 0 && values[archivedIdx]?.toLowerCase() === 'true';
        if (isArchived) continue;

        // Parse tags from comma-separated or semicolon-separated string
        let tags = [];
        if (tagsIdx >= 0 && values[tagsIdx]) {
          const tagString = values[tagsIdx].trim();
          if (tagString) {
            tags = tagString.split(/[,;]/).map(t => t.trim()).filter(t => t);
          }
        }

        // Parse favorite
        const isFavorite = favoriteIdx >= 0 && values[favoriteIdx]?.toLowerCase() === 'true';

        const title = (titleIdx >= 0 ? values[titleIdx] : values[0]) || 'Imported Entry';
        const username = usernameIdx >= 0 ? values[usernameIdx] : '';
        const password = passwordIdx >= 0 ? values[passwordIdx] : '';
        const url = urlIdx >= 0 ? values[urlIdx] : '';
        const otpAuth = otpAuthIdx >= 0 ? values[otpAuthIdx] : '';
        let notes = notesIdx >= 0 ? values[notesIdx] || '' : '';
        
        // Build notes including OTPAuth if present
        if (otpAuth) {
          notes = notes ? `${notes}\n\nOTP: ${otpAuth}` : `OTP: ${otpAuth}`;
        }

        const entry = {
          title: title,
          username: username,
          password: password,
          url: url,
          notes: notes,
          tags: tags,
          favorite: isFavorite,
          otpAuth: otpAuth,
          type: 'login' // Will be detected later
        };

        // Detect actual type based on content
        entry.type = detectEntryType(entry);

        data.push(entry);
      }
    }

    return data;
  };

  // Parse 1Password 1PUX format (ZIP containing JSON)
  const parse1Password1PUX = async (file) => {
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(file);
      
      // Find export.data file in the ZIP
      const exportDataFile = zip.file('export.data');
      if (!exportDataFile) {
        console.error('export.data not found in 1PUX file');
        return [];
      }

      const content = await exportDataFile.async('string');
      const data = JSON.parse(content);
      const items = [];

      // Iterate through accounts and vaults
      for (const account of (data.accounts || [])) {
        for (const vault of (account.vaults || [])) {
          for (const item of (vault.items || [])) {
            // Skip archived items
            if (item.state === 'archived') continue;

            const categoryUuid = item.categoryUuid || '001';
            const detectedType = ONEPASSWORD_CATEGORIES[categoryUuid] || 'login';
            const overview = item.overview || {};
            const details = item.details || {};

            // Extract login fields
            let username = '';
            let password = '';
            const loginFields = details.loginFields || [];
            
            for (const field of loginFields) {
              if (field.designation === 'username') {
                username = field.value || '';
              } else if (field.designation === 'password') {
                password = field.value || '';
              }
            }

            // Extract sections for additional data
            let additionalNotes = '';
            const sections = details.sections || [];
            for (const section of sections) {
              for (const field of (section.fields || [])) {
                if (field.value) {
                  const val = typeof field.value === 'object' ? 
                    (field.value.concealed || field.value.string || JSON.stringify(field.value)) : 
                    field.value;
                  additionalNotes += `${field.title || field.id}: ${val}\n`;
                }
              }
            }

            // Handle card data
            let cardData = {};
            if (detectedType === 'card' && details.sections) {
              for (const section of details.sections) {
                for (const field of (section.fields || [])) {
                  if (field.id?.includes('ccnum') || field.title?.toLowerCase().includes('number')) {
                    cardData.cardNumber = field.value?.creditCardNumber || field.value;
                  }
                  if (field.id?.includes('cardholder') || field.title?.toLowerCase().includes('holder')) {
                    cardData.cardholderName = field.value;
                  }
                  if (field.id?.includes('expiry') || field.title?.toLowerCase().includes('expir')) {
                    cardData.expiryDate = field.value?.monthYear || field.value;
                  }
                  if (field.id?.includes('cvv') || field.title?.toLowerCase().includes('cvv')) {
                    cardData.cvv = field.value?.concealed || field.value;
                  }
                }
              }
            }

            // Handle identity data
            let identityData = {};
            if (detectedType === 'identity' && details.sections) {
              for (const section of details.sections) {
                for (const field of (section.fields || [])) {
                  const fieldId = (field.id || '').toLowerCase();
                  const fieldTitle = (field.title || '').toLowerCase();
                  const value = typeof field.value === 'object' ? 
                    (field.value.string || field.value.date || JSON.stringify(field.value)) : field.value;
                  
                  if (fieldId.includes('firstname') || fieldTitle.includes('first name')) {
                    identityData.firstName = value;
                  }
                  if (fieldId.includes('lastname') || fieldTitle.includes('last name')) {
                    identityData.lastName = value;
                  }
                  if (fieldId.includes('email') || fieldTitle.includes('email')) {
                    identityData.email = value;
                  }
                  if (fieldId.includes('phone') || fieldTitle.includes('phone')) {
                    identityData.phone = value;
                  }
                  if (fieldId.includes('address') || fieldTitle.includes('address')) {
                    identityData.address = value;
                  }
                }
              }
            }

            const entry = {
              title: overview.title || 'Imported Entry',
              username: username,
              password: password,
              url: overview.url || (overview.urls?.[0]?.url) || '',
              notes: details.notesPlain || '',
              additionalNotes: additionalNotes,
              tags: overview.tags || [],
              favorite: (item.favIndex || 0) > 0,
              otpAuth: '',
              type: detectedType,
              // Additional type-specific data
              cardData: Object.keys(cardData).length > 0 ? cardData : undefined,
              identityData: Object.keys(identityData).length > 0 ? identityData : undefined
            };

            items.push(entry);
          }
        }
      }

      return items;
    } catch (error) {
      console.error('Error parsing 1PUX file:', error);
      // Try parsing as plain JSON (export.data content)
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        return parse1PasswordJSON(data);
      } catch (e) {
        console.error('Failed to parse as JSON too:', e);
        return [];
      }
    }
  };

  // Parse 1Password JSON format
  const parse1PasswordJSON = (data) => {
    const items = [];
    
    const processAccounts = (accounts) => {
      for (const account of (accounts || [])) {
        for (const vault of (account.vaults || [])) {
          for (const item of (vault.items || [])) {
            if (item.state === 'archived') continue;

            const categoryUuid = item.categoryUuid || '001';
            const detectedType = ONEPASSWORD_CATEGORIES[categoryUuid] || 'login';
            const overview = item.overview || {};
            const details = item.details || {};

            let username = '';
            let password = '';
            
            for (const field of (details.loginFields || [])) {
              if (field.designation === 'username') username = field.value || '';
              if (field.designation === 'password') password = field.value || '';
            }

            items.push({
              title: overview.title || 'Imported Entry',
              username: username,
              password: password,
              url: overview.url || '',
              notes: details.notesPlain || '',
              tags: overview.tags || [],
              favorite: (item.favIndex || 0) > 0,
              type: detectedType
            });
          }
        }
      }
    };

    if (data.accounts) {
      processAccounts(data.accounts);
    } else if (Array.isArray(data)) {
      // Simple array of items
      for (const item of data) {
        items.push({
          title: item.title || item.name || 'Imported Entry',
          username: item.username || item.login?.username || '',
          password: item.password || item.login?.password || '',
          url: item.url || item.location || item.urls?.[0]?.url || '',
          notes: item.notes || item.notesPlain || '',
          tags: item.tags || [],
          favorite: item.favorite || false,
          type: detectEntryType(item)
        });
      }
    }

    return items;
  };

  const parseLastPass = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // LastPass CSV: url,username,password,totp,extra,name,grouping,fav
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const urlIdx = headers.findIndex(h => h === 'url');
    const usernameIdx = headers.findIndex(h => h === 'username');
    const passwordIdx = headers.findIndex(h => h === 'password');
    const totpIdx = headers.findIndex(h => h === 'totp');
    const extraIdx = headers.findIndex(h => h === 'extra');
    const nameIdx = headers.findIndex(h => h === 'name');
    const groupIdx = headers.findIndex(h => h === 'grouping');
    const favIdx = headers.findIndex(h => h === 'fav');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 3) {
        const tags = [];
        if (groupIdx >= 0 && values[groupIdx]) {
          tags.push(...values[groupIdx].split('\\').filter(Boolean));
        }

        const entry = {
          title: nameIdx >= 0 ? values[nameIdx] : (values[5] || values[0] || 'Imported Entry'),
          username: usernameIdx >= 0 ? values[usernameIdx] : (values[1] || ''),
          password: passwordIdx >= 0 ? values[passwordIdx] : (values[2] || ''),
          url: urlIdx >= 0 ? values[urlIdx] : (values[0] || ''),
          notes: extraIdx >= 0 ? values[extraIdx] : (values[5] || ''),
          otpAuth: totpIdx >= 0 ? values[totpIdx] : '',
          tags: tags,
          favorite: favIdx >= 0 && values[favIdx] === '1',
          type: 'login'
        };
        
        entry.type = detectEntryType(entry);
        data.push(entry);
      }
    }

    return data;
  };

  // Chrome/Edge CSV parser: name,url,username,password or url,username,password
  const parseChromeCSV = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const nameIdx = headers.findIndex(h => h === 'name' || h === 'title');
    const urlIdx = headers.findIndex(h => h === 'url' || h === 'website');
    const usernameIdx = headers.findIndex(h => h === 'username' || h === 'user' || h === 'login');
    const passwordIdx = headers.findIndex(h => h === 'password' || h === 'pass');
    const noteIdx = headers.findIndex(h => h === 'note' || h === 'notes');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 3) {
        const url = urlIdx >= 0 ? values[urlIdx] : values[0];
        const entry = {
          title: nameIdx >= 0 ? values[nameIdx] : (extractDomainFromUrl(url) || 'Imported Entry'),
          username: usernameIdx >= 0 ? values[usernameIdx] : values[1],
          password: passwordIdx >= 0 ? values[passwordIdx] : values[2],
          url: url,
          notes: noteIdx >= 0 ? values[noteIdx] : '',
          type: 'login'
        };
        
        entry.type = detectEntryType(entry);
        data.push(entry);
      }
    }

    return data;
  };

  // Firefox CSV parser: "url","username","password","httpRealm","formActionOrigin","guid","timeCreated","timeLastUsed","timePasswordChanged"
  const parseFirefoxCSV = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const urlIdx = headers.findIndex(h => h === 'url' || h === 'hostname');
    const usernameIdx = headers.findIndex(h => h === 'username');
    const passwordIdx = headers.findIndex(h => h === 'password');
    const httpRealmIdx = headers.findIndex(h => h === 'httprealm');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 3) {
        const url = urlIdx >= 0 ? values[urlIdx] : values[0];
        const entry = {
          title: extractDomainFromUrl(url) || 'Imported Entry',
          username: usernameIdx >= 0 ? values[usernameIdx] : values[1],
          password: passwordIdx >= 0 ? values[passwordIdx] : values[2],
          url: url,
          notes: httpRealmIdx >= 0 && values[httpRealmIdx] ? `HTTP Realm: ${values[httpRealmIdx]}` : '',
          type: 'login'
        };
        
        entry.type = detectEntryType(entry);
        data.push(entry);
      }
    }

    return data;
  };

  // Safari CSV parser: Title,URL,Username,Password,Notes,OTPAuth
  const parseSafariCSV = (csv) => {
    const lines = csv.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const titleIdx = headers.findIndex(h => h === 'title');
    const urlIdx = headers.findIndex(h => h === 'url');
    const usernameIdx = headers.findIndex(h => h === 'username');
    const passwordIdx = headers.findIndex(h => h === 'password');
    const notesIdx = headers.findIndex(h => h === 'notes');
    const otpIdx = headers.findIndex(h => h === 'otpauth');

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 4) {
        const entry = {
          title: titleIdx >= 0 ? values[titleIdx] : 'Imported Entry',
          username: usernameIdx >= 0 ? values[usernameIdx] : '',
          password: passwordIdx >= 0 ? values[passwordIdx] : '',
          url: urlIdx >= 0 ? values[urlIdx] : '',
          notes: notesIdx >= 0 ? values[notesIdx] : '',
          otpAuth: otpIdx >= 0 ? values[otpIdx] : '',
          type: 'login'
        };
        
        entry.type = detectEntryType(entry);
        data.push(entry);
      }
    }

    return data;
  };

  // Helper function to extract domain from URL
  const extractDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      // Try to extract domain from URL-like string
      const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
      return match ? match[1] : url;
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportResult(null);

    try {
      let parsed = [];
      
      // Handle 1PUX files (ZIP format)
      if (importSource === '1password-1pux' || file.name.endsWith('.1pux')) {
        parsed = await parse1Password1PUX(file);
        setImportPreview(parsed);
        return;
      }

      const content = await file.text();

      if (importSource === 'bitwarden-json' || importSource === 'bitwarden') {
        // Try JSON first, then CSV
        if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
          parsed = parseBitwarden(content);
        } else {
          parsed = parseBitwardenCSV(content);
        }
      } else if (importSource === '1password') {
        parsed = parse1Password(content);
      } else if (importSource === 'lastpass') {
        parsed = parseLastPass(content);
      } else if (importSource === 'chrome' || importSource === 'edge') {
        // Chrome/Edge CSV: name,url,username,password
        parsed = parseChromeCSV(content);
      } else if (importSource === 'firefox') {
        // Firefox CSV: "url","username","password","httpRealm","formActionOrigin","guid","timeCreated","timeLastUsed","timePasswordChanged"
        parsed = parseFirefoxCSV(content);
      } else if (importSource === 'safari') {
        // Safari CSV: Title,URL,Username,Password,Notes,OTPAuth
        parsed = parseSafariCSV(content);
      } else {
        // Try to auto-detect format
        if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
          // JSON format
          const json = JSON.parse(content);
          if (json.items && (json.folders || json.encrypted !== undefined)) {
            // Bitwarden JSON format
            parsed = parseBitwarden(content);
          } else if (json.accounts || json.vaults) {
            // 1Password JSON format
            parsed = parse1PasswordJSON(json);
          } else if (Array.isArray(json)) {
            parsed = json.map(item => {
              const entry = {
                title: item.name || item.title || 'Imported Entry',
                username: item.username || item.login?.username || '',
                password: item.password || item.login?.password || '',
                url: item.url || item.login?.uris?.[0]?.uri || '',
                notes: item.notes || '',
                tags: item.tags || [],
                favorite: item.favorite || false,
                type: 'login'
              };
              entry.type = detectEntryType(entry);
              return entry;
            });
          }
        } else {
          // CSV format - try to detect by headers
          const firstLine = content.split('\n')[0].toLowerCase();
          if (firstLine.includes('login_uri') || firstLine.includes('login_username')) {
            parsed = parseBitwardenCSV(content);
          } else if (firstLine.includes('httpRealm') || firstLine.includes('formactionorigin')) {
            parsed = parseFirefoxCSV(content);
          } else if (firstLine.includes('title') && firstLine.includes('otpauth')) {
            parsed = parse1PasswordCSV(content);
          } else {
            parsed = parseCSV(content);
          }
        }
      }

      setImportPreview(parsed);
    } catch (error) {
      console.error('Error parsing file:', error);
      setImportPreview([]);
    }
  };

  const handleImport = async () => {
    if (importPreview.length === 0) return;

    setImporting(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');

      // Preparar todos los items para bulk import
      const items = importPreview.map(item => {
        // Combine existing tags with 'imported' tag and type tag
        const itemTags = Array.isArray(item.tags) ? item.tags : [];
        const typeTag = item.type && item.type !== 'login' ? item.type : null;
        const allTags = [...new Set([...itemTags, 'imported', typeTag].filter(Boolean))];

        // Build notes with additional notes
        let fullNotes = item.notes || '';
        if (item.additionalNotes) {
          fullNotes = fullNotes ? `${fullNotes}\n\n--- Additional Fields ---\n${item.additionalNotes}` : item.additionalNotes;
        }

        // Build data object based on type
        const data = {
          type: item.type || 'login',
          username: item.username || '',
          password: item.password || '',
          url: item.url || '',
          notes: fullNotes,
          vault: 'personal',
          tags: allTags,
          favorite: item.favorite || false,
          otpAuth: item.otpAuth || ''
        };

        // Add card data if present
        if (item.cardData) {
          data.cardNumber = item.cardData.cardNumber || '';
          data.cardholderName = item.cardData.cardholderName || '';
          data.expiryMonth = item.cardData.expiryMonth || '';
          data.expiryYear = item.cardData.expiryYear || '';
          data.expiryDate = item.cardData.expiryDate || '';
          data.cvv = item.cardData.cvv || '';
          data.brand = item.cardData.brand || '';
        }

        // Add identity data if present
        if (item.identityData) {
          data.firstName = item.identityData.firstName || '';
          data.lastName = item.identityData.lastName || '';
          data.email = item.identityData.email || '';
          data.phone = item.identityData.phone || '';
          data.address = item.identityData.address || '';
          data.city = item.identityData.city || '';
          data.state = item.identityData.state || '';
          data.postalCode = item.identityData.postalCode || '';
          data.country = item.identityData.country || '';
          data.company = item.identityData.company || '';
          data.ssn = item.identityData.ssn || '';
          data.passportNumber = item.identityData.passportNumber || '';
          data.licenseNumber = item.identityData.licenseNumber || '';
        }

        return {
          name: item.title,
          description: `${item.type || 'login'} - ${item.title}`,
          data: data,
          tags: allTags
        };
      });

      // Send all items at once - server handles it as a background thread
      const response = await axios.post(`${API_BASE}/api/data/bulk-import`, {
        rfidId,
        items
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // Just for the initial request, not the whole operation
      });

      if (response.data.threadId) {
        // Thread-based: track progress via WebSocket + fallback polling
        startThreadPolling(response.data.threadId);

        // Wait for completion by watching threadStatus state changes
        const waitForCompletion = () => new Promise((resolve) => {
          // Check initial status
          const checkStatus = async () => {
            const result = await pollThreadStatus(response.data.threadId);
            if (result) return resolve(result);
          };
          
          // Let the existing startThreadPolling handle WS/polling
          // We just need to periodically check if the thread finished
          const checkInterval = setInterval(async () => {
            const result = await pollThreadStatus(response.data.threadId);
            if (result) {
              clearInterval(checkInterval);
              resolve(result);
            }
          }, 3000);

          // Initial check after a small delay
          setTimeout(checkStatus, 1000);

          // Timeout after 10 minutes
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve({ status: 'timeout' });
          }, 600000);
        });

        const finalResult = await waitForCompletion();

        if (finalResult.status === 'completed') {
          setImportResult({
            success: finalResult.result?.success || 0,
            failed: finalResult.result?.failed || 0,
            total: finalResult.result?.total || items.length
          });
          if (onImportComplete) onImportComplete();
        } else if (finalResult.status === 'failed') {
          setImportResult({
            success: 0,
            failed: items.length,
            total: items.length,
            error: finalResult.error || 'Error en la importación'
          });
        } else if (finalResult.status === 'cancelled') {
          setImportResult({
            success: 0,
            failed: items.length,
            total: items.length,
            error: 'Importación cancelada'
          });
        } else {
          // Timeout or direct response (non-threaded fallback)
          setImportResult({
            success: response.data.success || 0,
            failed: response.data.failed || 0,
            total: items.length,
            error: finalResult.status === 'timeout' ? 'Tiempo de espera agotado, la importación continúa en el servidor' : undefined
          });
          if (onImportComplete) onImportComplete();
        }
      } else {
        // Direct response (legacy/fallback)
        setImportResult({
          success: response.data.success || items.length,
          failed: response.data.failed || 0,
          total: items.length
        });
        if (onImportComplete) onImportComplete();
      }

    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: 0,
        failed: importPreview.length,
        total: importPreview.length,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setImporting(false);
      setThreadId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');

      // Use thread-based bulk export
      const response = await axios.post(
        `${API_BASE}/api/data/bulk-export`,
        { rfidId, format: exportFormat },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      if (response.data.threadId) {
        // Thread-based: track progress
        startThreadPolling(response.data.threadId);

        // Wait for completion - startThreadPolling already handles WS + 2s polling
        // This just checks periodically at a slower rate as a safety net
        const waitForCompletion = () => new Promise((resolve) => {
          const checkInterval = setInterval(async () => {
            const result = await pollThreadStatus(response.data.threadId);
            if (result) {
              clearInterval(checkInterval);
              resolve(result);
            }
          }, 3000);

          // Timeout after 5 minutes
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve({ status: 'timeout' });
          }, 300000);
        });

        const finalResult = await waitForCompletion();

        if (finalResult.status === 'completed' && finalResult.result?.data) {
          const exportData = finalResult.result.data;
          let exportContent = '';
          let filename = '';
          let mimeType = '';

          if (exportFormat === 'csv') {
            exportContent = typeof exportData === 'string' ? exportData : JSON.stringify(exportData, null, 2);
            filename = `rfid-passwords-${new Date().toISOString().split('T')[0]}.csv`;
            mimeType = 'text/csv';
          } else if (exportFormat === 'encrypted') {
            exportContent = JSON.stringify(exportData, null, 2);
            filename = `rfid-backup-${new Date().toISOString().split('T')[0]}.rfidvault`;
            mimeType = 'application/json';
          } else {
            exportContent = JSON.stringify(exportData, null, 2);
            filename = `rfid-passwords-${new Date().toISOString().split('T')[0]}.json`;
            mimeType = 'application/json';
          }

          // Download file
          const blob = new Blob([exportContent], { type: mimeType });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else if (finalResult.status === 'failed') {
          console.error('Export failed:', finalResult.error);
        }
      } else {
        // Fallback: direct response (legacy)
        console.warn('Server did not return threadId for export');
      }

    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
      setThreadId(null);
    }
  };

  const getSourceInstructions = () => {
    switch (importSource) {
      case 'chrome':
        return t('importExport.instructions.chrome', 'In Chrome, go to Settings > Passwords > Export passwords. The CSV will have: name, url, username, password.');
      case 'firefox':
        return t('importExport.instructions.firefox', 'In Firefox, go to about:logins > ⋮ menu > Export Passwords. The CSV format includes: url, username, password.');
      case 'edge':
        return t('importExport.instructions.edge', 'In Edge, go to Settings > Passwords > Export passwords. Same format as Chrome CSV.');
      case 'safari':
        return t('importExport.instructions.safari', 'In Safari on Mac, go to Preferences > Passwords > Export Passwords. Format: Title, URL, Username, Password, Notes, OTPAuth.');
      case 'bitwarden':
        return t('importExport.instructions.bitwarden', 'In Bitwarden, go to Tools > Export. CSV supports logins and notes. For cards and identities, use JSON format.');
      case 'bitwarden-json':
        return t('importExport.instructions.bitwardenJson', 'Export from Bitwarden as JSON to include all item types: Logins, Cards, Identities, and Secure Notes.');
      case '1password':
        return t('importExport.instructions.1password', 'In 1Password, the CSV export only includes Login and Password items. For all types (cards, identities, notes), use 1PUX format.');
      case '1password-1pux':
        return t('importExport.instructions.1password1pux', 'In 1Password 8, go to ⋯ > Export > Choose 1PUX format. This includes ALL item types: Logins, Cards, Identities, Secure Notes, SSH Keys, and more.');
      case 'lastpass':
        return t('importExport.instructions.lastpass', 'In LastPass, go to Advanced Options > Export > CSV File.');
      default:
        return t('importExport.instructions.generic', 'Select a CSV or JSON file with your passwords. The importer will try to detect the format automatically.');
    }
  };

  return (
    <div className="import-export-overlay" onClick={onClose}>
      <div className="import-export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ie-header">
          <div className="ie-tabs">
            <button
              className={`ie-tab ${activeTab === 'import' ? 'active' : ''}`}
              onClick={() => setActiveTab('import')}
            >
              <FiUpload size={18} />
              {t('importExport.import')}
            </button>
            <button
              className={`ie-tab ${activeTab === 'export' ? 'active' : ''}`}
              onClick={() => setActiveTab('export')}
            >
              <FiDownload size={18} />
              {t('importExport.export')}
            </button>
          </div>
          <button className="ie-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <div className="ie-content">
          {activeTab === 'import' && (
            <div className="ie-import">
              <div className="ie-section">
                <label>{t('importExport.selectSource')}</label>
                <div className="ie-source-grid">
                  {importSources.map(source => (
                    <button
                      key={source.id}
                      className={`ie-source-btn ${importSource === source.id ? 'selected' : ''}`}
                      onClick={() => {
                        setImportSource(source.id);
                        setImportFile(null);
                        setImportPreview([]);
                        setImportResult(null);
                      }}
                    >
                      <source.icon size={20} />
                      <span>{source.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="ie-section">
                <div className="ie-instructions">
                  <FiAlertTriangle size={16} />
                  <p>{getSourceInstructions()}</p>
                </div>
              </div>

              <div className="ie-section">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv,.json,.txt,.1pux,.zip"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="ie-file-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload size={18} />
                  {importFile ? importFile.name : t('importExport.selectFile')}
                </button>
              </div>

              {importPreview.length > 0 && !importResult && (
                <div className="ie-section">
                  <div className="ie-preview">
                    <h4>{t('importExport.preview')} ({importPreview.length} {t('importExport.entries')})</h4>
                    {/* Type summary */}
                    <div className="ie-type-summary">
                      {(() => {
                        const typeCounts = importPreview.reduce((acc, item) => {
                          const type = item.type || 'login';
                          acc[type] = (acc[type] || 0) + 1;
                          return acc;
                        }, {});
                        return Object.entries(typeCounts).map(([type, count]) => (
                          <span key={type} className={`ie-type-badge ie-type-${type}`}>
                            {type}: {count}
                          </span>
                        ));
                      })()}
                    </div>
                    <div className="ie-preview-list">
                      {importPreview.slice(0, 5).map((item, index) => (
                        <div key={index} className="ie-preview-item">
                          <span className={`ie-preview-type ie-type-${item.type || 'login'}`}>
                            {item.type || 'login'}
                          </span>
                          <span className="ie-preview-title">{item.title}</span>
                          <span className="ie-preview-username">
                            {item.username || (item.type === 'secureNote' ? '(note)' : '')}
                          </span>
                          {item.favorite && <span className="ie-preview-fav">★</span>}
                        </div>
                      ))}
                      {importPreview.length > 5 && (
                        <div className="ie-preview-more">
                          +{importPreview.length - 5} {t('importExport.moreEntries')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {importResult && (
                <div className="ie-section">
                  <div className={`ie-result ${importResult.error ? 'warning' : 'success'}`}>
                    <FiCheck size={24} />
                    <div>
                      <h4>{t('importExport.importComplete')}</h4>
                      <p>
                        {importResult.success} {t('importExport.imported')}, {importResult.failed} {t('importExport.failed')}
                      </p>
                      {importResult.error && (
                        <p className="ie-result-error">{importResult.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Thread progress bar */}
              {importing && threadId && (
                <div className="ie-section">
                  <div className="ie-thread-progress">
                    <div className="ie-progress-header">
                      <span className="ie-progress-label">
                        {threadMessage || 'Procesando...'}
                      </span>
                      <span className="ie-progress-percent">{threadProgress}%</span>
                    </div>
                    <div className="ie-progress-bar">
                      <div 
                        className="ie-progress-fill" 
                        style={{ width: `${threadProgress}%` }}
                      />
                    </div>
                    {threadStatus === 'running' && (
                      <button className="ie-btn-cancel" onClick={cancelThread}>
                        <FiX size={14} /> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="ie-actions">
                <button className="ie-btn-secondary" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button
                  className="ie-btn-primary"
                  onClick={handleImport}
                  disabled={importPreview.length === 0 || importing || importResult}
                >
                  {importing ? `${t('importExport.importing')} ${threadProgress > 0 ? threadProgress + '%' : ''}` : t('importExport.importNow')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'export' && (
            <div className="ie-export">
              <div className="ie-section">
                <label>{t('importExport.selectFormat')}</label>
                <div className="ie-format-list">
                  {exportFormats.map(format => (
                    <button
                      key={format.id}
                      className={`ie-format-btn ${exportFormat === format.id ? 'selected' : ''}`}
                      onClick={() => setExportFormat(format.id)}
                    >
                      <div className="ie-format-info">
                        <span className="ie-format-name">{format.name}</span>
                        <span className="ie-format-desc">{format.description}</span>
                      </div>
                      {exportFormat === format.id && <FiCheck size={18} />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ie-section">
                <div className="ie-warning">
                  <FiAlertTriangle size={16} />
                  <p>{t('importExport.exportWarning')}</p>
                </div>
              </div>

              {/* Thread progress bar for export */}
              {exporting && threadId && (
                <div className="ie-section">
                  <div className="ie-thread-progress">
                    <div className="ie-progress-header">
                      <span className="ie-progress-label">
                        {threadMessage || 'Exportando...'}
                      </span>
                      <span className="ie-progress-percent">{threadProgress}%</span>
                    </div>
                    <div className="ie-progress-bar">
                      <div 
                        className="ie-progress-fill ie-progress-export" 
                        style={{ width: `${threadProgress}%` }}
                      />
                    </div>
                    {threadStatus === 'running' && (
                      <button className="ie-btn-cancel" onClick={cancelThread}>
                        <FiX size={14} /> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="ie-actions">
                <button className="ie-btn-secondary" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button
                  className="ie-btn-primary"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  <FiDownload size={18} />
                  {exporting ? `${t('importExport.exporting')} ${threadProgress > 0 ? threadProgress + '%' : ''}` : t('importExport.exportNow')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportExport;
